// Finding and opening sims. A domain lists its runs in `sims/<domain>/index.json` (a pharma run
// directory can carry its own); every run has a `header.json` whose `format` names its contract.
// A scene asks for candidates in order of preference and gets the first that exists.

import { CellsSim } from './cells.ts';
import { DynamicsSim } from './dynamics.ts';
import { NotFound, SimFiles, type SimSource } from './files.ts';
import { HemoSim } from './hemo.ts';
import { PharmaSim } from './pharma.ts';
import { FORMATS, type CellsHeader, type CommonHeader, type DynamicsHeader, type HemoHeader, type IndexAnnotation, type IndexEntry, type PharmaHeader, type SimFormat } from './types.ts';

export type OpenSim =
  | { format: 'hemo'; path: string; header: HemoHeader; sim: HemoSim }
  | { format: 'dynamics'; path: string; header: DynamicsHeader; sim: DynamicsSim }
  | { format: 'cells'; path: string; header: CellsHeader; sim: CellsSim }
  | { format: 'pharma'; path: string; header: PharmaHeader; sim: PharmaSim };

export function detectFormat(h: Pick<CommonHeader, 'format'>): SimFormat | null {
  return FORMATS[h.format] ?? null;
}

export const STAND_IN = /(^|[^a-z])(synth|synthetic|probe)/i;

/**
 * True for anything that isn't a real model output: the header says `illustrative`, or the run's
 * name says synth, synthetic or probe. Such a run may drive the picture, but the scene stays a
 * preview and its Methods say what it is.
 */
export function isStandIn(h: CommonHeader, path: string): boolean {
  return h.provenance === 'illustrative' || STAND_IN.test(path) || STAND_IN.test(h.id) || STAND_IN.test(h.scenario?.label ?? '');
}

/** A domain's (or run's) index, or null when it has none. Entry paths come back relative to sims/. */
export async function readIndex(src: SimSource, dir: string): Promise<IndexEntry[] | null> {
  const rows = await new SimFiles(src, dir).maybeJson<IndexEntry[]>('index.json');
  if (!Array.isArray(rows)) return null;
  return rows.filter((r) => r && typeof r.path === 'string').map((r) => ({ ...r, path: `${dir}/${r.path}`.replace(/\/+/g, '/') }));
}

const hex = (b: ArrayBuffer) => Array.from(new Uint8Array(b), (x) => x.toString(16).padStart(2, '0')).join('');

/**
 * The label and Methods text a run's index row gives it, when the row binds them to the run's header
 * (`header_sha256`) and the header still hashes to that; else null. A header rewritten since the
 * row was, or a page without Web Crypto, keeps the header's own text.
 */
export async function annotationOf(src: SimSource, path: string): Promise<IndexAnnotation | null> {
  const dir = path.slice(0, Math.max(0, path.indexOf('/')));
  const row = dir ? (await readIndex(src, dir))?.find((r) => r.path === path) : undefined;
  const subtle = globalThis.crypto?.subtle;
  if (typeof row?.header_sha256 !== 'string' || !subtle) return null;
  if (hex(await subtle.digest('SHA-256', await new SimFiles(src, path).bytes('header.json'))) !== row.header_sha256.toLowerCase()) return null;
  const label = typeof row.scenario === 'object' && typeof row.scenario.label === 'string' ? row.scenario.label : undefined;
  const methods = typeof row.methods === 'string' ? row.methods : undefined;
  return label || methods ? { ...(label ? { label } : {}), ...(methods ? { methods } : {}) } : null;
}

/** A run's header, or null when the run doesn't exist. */
export async function readHeader(src: SimSource, path: string): Promise<CommonHeader | null> {
  const h = await new SimFiles(src, path).maybeJson<CommonHeader>('header.json');
  if (h && (typeof h.format !== 'string' || typeof h.id !== 'string')) throw new Error(`sims/${path}/header.json has no format or id`);
  return h;
}

const opened = new Map<string, Promise<OpenSim>>();

/** Open a run with its contract's reader. Readers load what they need lazily; this loads the header (and a network's graph). */
export function openSim(src: SimSource, path: string): Promise<OpenSim> {
  const key = `${src.base}${path}`;
  let p = opened.get(key);
  if (!p) {
    p = (async (): Promise<OpenSim> => {
      const header = await readHeader(src, path);
      if (!header) throw new NotFound(`sims/${path}: no header.json`);
      const files = new SimFiles(src, path);
      const format = detectFormat(header);
      switch (format) {
        case 'hemo':
          return { format, path, header, sim: await HemoSim.open(files, header) };
        case 'dynamics':
          return { format, path, header, sim: await DynamicsSim.open(files, header as DynamicsHeader) };
        case 'cells':
          return { format, path, header, sim: await CellsSim.open(files, header as CellsHeader) };
        case 'pharma':
          return { format, path, header, sim: await PharmaSim.open(files, header as PharmaHeader) };
        default:
          throw new Error(`sims/${path}: unknown format ${header.format}`);
      }
    })();
    opened.set(key, p);
    p.catch(() => opened.delete(key));
  }
  return p;
}

/** Forget opened runs (tests). */
export function clearOpened() {
  opened.clear();
}

/** The first candidate that exists, opened; null when none does. Errors other than "missing" propagate. */
export async function resolveFirst(src: SimSource, candidates: string[]): Promise<OpenSim | null> {
  for (const path of candidates) {
    try {
      return await openSim(src, path);
    } catch (err) {
      if (!(err instanceof NotFound)) throw err;
    }
  }
  return null;
}

/** Whether any candidate exists, without opening a reader (choice availability). */
export async function anyExists(src: SimSource, candidates: string[]): Promise<string | null> {
  for (const path of candidates) if (await readHeader(src, path).catch(() => null)) return path;
  return null;
}
